import React from 'react';
import { CheckCircle2, Sparkles, PhoneCall } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';

/**
 * ThankYouDialog
 *
 * Final confirmation modal after a successful submit. Intentionally
 * minimal — checkmark, candidate name, the role they just applied to,
 * a single Close button. Closing dismisses both this dialog and the
 * detail dialog underneath (parent handles that).
 */
export function ThankYouDialog({
  candidateName,
  jobTitle,
  companyName,
  autoScreenScheduled = false,
  autoScreenMessage = '',
  onClose,
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md">
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <div className="relative">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
              <CheckCircle2 className="h-9 w-9" />
            </div>
            {/* tiny sparkles for delight */}
            <Sparkles className="absolute -right-2 -top-1 h-4 w-4 text-amber-500" />
            <Sparkles className="absolute -bottom-1 -left-2 h-3 w-3 text-amber-500" />
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-bold tracking-tight">Thank you!</h2>
            <p className="text-sm text-muted-foreground">
              {candidateName ? (
                <>
                  Your application has been received,{' '}
                  <span className="font-medium text-foreground">
                    {candidateName}
                  </span>
                  .
                </>
              ) : (
                'Your application has been received.'
              )}
            </p>
          </div>

          {(jobTitle || companyName) && (
            <div className="rounded-md border border-border/40 bg-muted/30 px-4 py-3 text-sm">
              <p className="font-medium">{jobTitle}</p>
              {companyName && (
                <p className="text-xs text-muted-foreground">{companyName}</p>
              )}
            </div>
          )}

          {autoScreenScheduled ? (
            <div className="w-full rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-left dark:border-emerald-900 dark:bg-emerald-950/40">
              <div className="flex items-start gap-2">
                <PhoneCall className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                    Expect a call shortly
                  </p>
                  <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-400">
                    {autoScreenMessage ||
                      'Our AI recruiter will call you shortly for a quick screening — please keep your phone handy.'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              The recruiter will review your profile and reach out by email
              if there's a fit. You can browse other open roles in the
              meantime.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} className="w-full">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
